import React, { useState } from "react";
import { Download, FileSpreadsheet, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

function downloadCSV(path, filename) {
  const token = localStorage.getItem("lpg_token");
  return fetch(`${BASE}/api${path}`, {
    method: "GET",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then(async (r) => {
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        throw new Error(`${r.status} — ${txt || r.statusText}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
}

function DownloadButton({ label, sub, path, filename, testid, adminOnly, isAdmin, onError }) {
  const [busy, setBusy] = useState(false);
  if (adminOnly && !isAdmin) return null;
  const click = async () => {
    setBusy(true);
    try {
      await downloadCSV(path, filename);
    } catch (e) {
      onError?.(e.message || "Download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={click}
      disabled={busy}
      data-testid={testid}
      className="w-full text-left flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-sm hover:border-black hover:bg-gray-50 transition disabled:opacity-50"
    >
      <div className="w-9 h-9 shrink-0 rounded-sm bg-[#0A0A0A] text-white flex items-center justify-center">
        <FileSpreadsheet size={18} strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-base">{label}</div>
        {sub && <div className="text-xs text-gray-500 mt-0.5 truncate">{sub}</div>}
      </div>
      <Download size={18} className={`shrink-0 mt-1 ${busy ? "animate-pulse" : ""}`} />
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-flat p-5">
      <div className="small-label text-gray-500 mb-1">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">{children}</div>
    </div>
  );
}

export default function Downloads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [err, setErr] = useState("");

  return (
    <div className="space-y-6" data-testid="downloads-page">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">
          Downloads
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          One-click CSV exports for everything in your books — open in Excel, Google Sheets or accounting software.
        </p>
      </div>

      {err && (
        <div className="card-flat p-3 flex items-center gap-2 bg-[#FEE2E2] border-[#FECACA] text-[#B91C1C] text-sm" data-testid="download-error">
          <AlertCircle size={16} /> {err}
          <button className="ml-auto text-xs underline" onClick={() => setErr("")}>dismiss</button>
        </div>
      )}

      <Section title="Transactions">
        <DownloadButton
          label="All Purchase Entries"
          sub="Vendor purchases — filled, MT, rates, amounts"
          path="/exports/purchases"
          filename="purchases.csv"
          testid="dl-purchases"
          adminOnly
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="All Sale Entries"
          sub="Customer sales — filled, MT, rates, amounts"
          path="/exports/sales"
          filename="sales.csv"
          testid="dl-sales"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="All Transfer Entries"
          sub="Godown to godown movements"
          path="/exports/transfers"
          filename="transfers.csv"
          testid="dl-transfers"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Adjustment / Defective / Opening"
          sub="All non-trading stock corrections"
          path="/exports/adjustments"
          filename="adjustments.csv"
          testid="dl-adjustments"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="All Challan Entries"
          sub="One row per challan with totals & vehicle"
          path="/exports/challans"
          filename="challans.csv"
          testid="dl-challans"
          isAdmin={isAdmin}
          onError={setErr}
        />
      </Section>

      <Section title="Money">
        <DownloadButton
          label="All Payment-In Entries"
          sub="Money received from customers / vendors"
          path="/exports/payments?direction=received"
          filename="payments_in.csv"
          testid="dl-payments-in"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="All Payment-Out Entries"
          sub="Money paid to vendors / customers"
          path="/exports/payments?direction=paid"
          filename="payments_out.csv"
          testid="dl-payments-out"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="All Expenses"
          sub="Cash expenses by holder & category"
          path="/exports/expenses"
          filename="expenses.csv"
          testid="dl-expenses"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Cash Holder Transfers"
          sub="Holder-to-holder / holder-to-admin movements"
          path="/exports/cash-holder"
          filename="cash_holder_transfers.csv"
          testid="dl-cash-holder"
          isAdmin={isAdmin}
          onError={setErr}
        />
      </Section>

      <Section title="Pending & Reports">
        <DownloadButton
          label="Pending Payments"
          sub="Customer dues + Vendor dues — current"
          path="/exports/pending-payments"
          filename="pending_payments.csv"
          testid="dl-pending-payments"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Pending Cylinders (MT)"
          sub="Per-party brand & size pending"
          path="/exports/pending-cylinders"
          filename="pending_cylinders.csv"
          testid="dl-pending-cyl"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="All Daily Tally Records"
          sub="Every tally with system vs ground & diff"
          path="/exports/tally"
          filename="daily_tally.csv"
          testid="dl-tally"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Daily Closing Stock (today)"
          sub="Live closing stock per godown / brand / size"
          path="/exports/daily-closing"
          filename="daily_closing_today.csv"
          testid="dl-daily-closing"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="All Attendance Entries"
          sub="Date-wise attendance for every employee"
          path="/exports/attendance"
          filename="attendance.csv"
          testid="dl-attendance"
          isAdmin={isAdmin}
          onError={setErr}
        />
      </Section>

      <Section title="Masters">
        <DownloadButton
          label="Customers & Vendors"
          sub="Names, phone, address, opening balance"
          path="/exports/masters?kind=parties"
          filename="masters_parties.csv"
          testid="dl-masters-parties"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Employees"
          sub="Staff with role, wage type & salary"
          path="/exports/masters?kind=employees"
          filename="masters_employees.csv"
          testid="dl-masters-employees"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Brands"
          sub="All brand names"
          path="/exports/masters?kind=brands"
          filename="masters_brands.csv"
          testid="dl-masters-brands"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Sizes"
          sub="All cylinder sizes with KG"
          path="/exports/masters?kind=sizes"
          filename="masters_sizes.csv"
          testid="dl-masters-sizes"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Godowns"
          sub="All warehouse locations"
          path="/exports/masters?kind=godowns"
          filename="masters_godowns.csv"
          testid="dl-masters-godowns"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Cash Holders"
          sub="On-site managers handling cash"
          path="/exports/masters?kind=holders"
          filename="masters_cash_holders.csv"
          testid="dl-masters-holders"
          isAdmin={isAdmin}
          onError={setErr}
        />
        <DownloadButton
          label="Expense Categories"
          sub="All expense categories"
          path="/exports/masters?kind=expense_categories"
          filename="masters_expense_categories.csv"
          testid="dl-masters-categories"
          isAdmin={isAdmin}
          onError={setErr}
        />
      </Section>
    </div>
  );
}
